import { ArrowDown, ArrowUp, Info, Minus } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  formatEmissionValue,
  formatGap as formatGapBase,
  formatValue as formatValueBase,
} from "../domains/formatting";

const fmtValue = (v) => formatValueBase(v, "n.a");
const fmtGap = (v) => formatGapBase(v, "n.a");
const IMPROVEMENT_DIRECTION_LABELS = {
  1: "1 - Higher is better",
  "-1": "-1 - Lower is better",
};

const formatImprovementDirection = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return IMPROVEMENT_DIRECTION_LABELS[value] ?? String(value);
};

const MetricStatNumbers = ({ row, unit = "", naMode = false }) => {
  const { t } = useTran();
  const isGood = Number.isFinite(row.gapPct) && row.gapPct >= 0;
  const gapTone = !Number.isFinite(row.gapPct)
    ? "text-muted-foreground"
    : isGood
      ? "text-emerald-600 dark:text-emerald-300"
      : "text-blue-600 dark:text-blue-300";
  const GapIcon = !Number.isFinite(row.gap)
    ? Minus
    : row.gap > 0
      ? ArrowUp
      : row.gap < 0
        ? ArrowDown
        : Minus;

  const unitSuffix = !naMode && unit ? ` ${unit}` : "";
  const showValue = (v) =>
    naMode ? "n.a" : `${fmtValue(v)}${Number.isFinite(v) ? unitSuffix : ""}`;
  const showGap = (v) =>
    naMode ? "n.a" : `${fmtGap(v)}${Number.isFinite(v) ? unitSuffix : ""}`;
  const showCarbonEmissionReduction =
    !naMode && Number.isFinite(row.carbonEmissionReduction);
  const showGapRow = naMode || Number.isFinite(row.gap);
  const improvementDirection = formatImprovementDirection(
    row.improvementDirection,
  );
  const improvementDirectionTip = improvementDirection
    ? `${t("Improvement Direction")}: ${t(improvementDirection)}`
    : "";
  const carbonInfoIcon = (
    <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
  );

  return (
    <div
      className={cn(
        "max-w-full whitespace-nowrap text-xs tabular-nums",
        showCarbonEmissionReduction ? "w-[240px]" : "w-[200px]",
      )}
    >
      <div className="text-right">
        <span className="text-muted-foreground">{t("Target")}: </span>
        <span className="font-bold text-foreground">
          {showValue(row.target)}
        </span>
      </div>
      <div className="text-right">
        <span className="text-muted-foreground">{t("Actual")}: </span>
        <span className="font-bold text-foreground">
          {showValue(row.actual)}
        </span>
      </div>
      {showGapRow ? (
        <div
          className={cn(
            "flex items-center justify-end gap-1 font-bold",
            gapTone,
          )}
        >
          {!naMode && <GapIcon className="h-3.5 w-3.5" />}
          {showGap(row.gap)}
        </div>
      ) : null}
      {showCarbonEmissionReduction ? (
        <div className="mt-0.5 flex max-w-full items-center justify-end gap-1 text-right text-[11px] font-bold leading-tight text-emerald-600 dark:text-emerald-300">
          {improvementDirectionTip ? (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex shrink-0 cursor-help items-center outline-none"
                    aria-label={improvementDirectionTip}
                  >
                    {carbonInfoIcon}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" align="start">
                  {improvementDirectionTip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            carbonInfoIcon
          )}
          <span className="whitespace-nowrap">
            {t("CO2e Reduction")} :{" "}
            {formatEmissionValue(row.carbonEmissionReduction)} kgCO₂e
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default MetricStatNumbers;
